import cn from 'classnames';
import { forwardRef } from 'react';
import toast from 'react-hot-toast/headless';
import { useTranslation } from 'react-i18next';

import { IconSearch } from 'components/UI/Icons/Search';
import { Input } from 'components/UI/Tailwind/Input';
import type { UseSearchResponse } from 'helpers/search';

/**
 * Longest phrase a search box accepts. Mirrors the API's server-side cap
 * (`SearchPhrase` truncates at 200): a pasted wall of text — e.g. an entire
 * role spec — tokenizes into hundreds of terms and can pin an ES query past
 * its timeout while the UI shows nothing wrong.
 */
export const SEARCH_PHRASE_MAX_LENGTH = 200;

export const SearchInput = forwardRef<
  HTMLInputElement,
  Pick<Input, 'className' | 'loading' | 'prefix' | 'disabled'> &
    Pick<Required<Input>, 'placeholder'> &
    Pick<Partial<UseSearchResponse>, 'reset'> &
    Pick<UseSearchResponse, 'onSearch' | 'phrase'> & {
      'data-test'?: string;
    }
>(({ className, loading, onSearch, phrase, reset, ...rest }, ref) => {
  const { t } = useTranslation();

  return (
    <Input
      ref={ref}
      autoComplete="off"
      className={cn('bg-white', className)}
      loading={Boolean(phrase) && loading}
      maxLength={SEARCH_PHRASE_MAX_LENGTH}
      onChange={onSearch}
      onPaste={(event) => {
        // Native maxLength truncates oversized pastes silently; tell the
        // user their query was cut so a shortened search isn't mistaken
        // for a complete one.
        const input = event.currentTarget;
        const pasted = event.clipboardData.getData('text');
        const selection =
          (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
        const nextLength = input.value.length - selection + pasted.length;
        if (nextLength > SEARCH_PHRASE_MAX_LENGTH) {
          toast(
            t('generalSearch.phraseTruncated', {
              count: SEARCH_PHRASE_MAX_LENGTH,
            }),
          );
        }
      }}
      onReset={reset}
      prefix={<IconSearch />}
      size={32}
      value={phrase}
      {...rest}
    />
  );
});
